import { getTypeText } from "../../../../core/models/entities/BaseLogo";
import {
  OrderLine,
  OrderLineDesign,
  OrderLineLogo,
} from "../../../../core/models/entities/Order";
import React, { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { Role } from "../../../../core/models/entities/Role";
import { enviroment } from "../../../../enviroment";

const getPriceString = (price?: number, currency?: string) =>
  price ? `${currency || "DKK"} ${price.toFixed(2)}` : "";

// For a line with an admin-agreed price: 0 is a real, agreed amount ("DKK
// 0.00"), not "no price" - getPriceString's empty string for 0 would hide
// that an agreement exists. Used only in the agreed branch, so every line
// without an agreement renders exactly as before.
const getAgreedPriceString = (price: number, currency?: string) =>
  `${currency || "DKK"} ${price.toFixed(2)}`;

// Display-only mapping for the TYPE column. Order line `type` values are data
// that other code compares against (e.g. groupOrderLinesForDisplay,
// OrderLineList), so the raw value stays untouched everywhere else - only the
// rendering here is translated. Unknown values render as-is (e.g. "Transfer",
// which is a product term in both languages).
const ORDER_LINE_TYPE_KEYS: Record<string, string> = {
  "Additional Fee": "orderLineType.additionalFee",
  AdditionalFee: "orderLineType.additionalFee",
  Heating: "orderLineType.heating",
  Packaging: "orderLineType.packaging",
};

export function orderLineTypeText(
  type: string | undefined,
  t: (key: string) => string,
): string {
  if (!type) return "";
  const key = ORDER_LINE_TYPE_KEYS[type];
  return key ? t(key) : type;
}

// Display-only mapping for line TITLES. Titles are order DATA (they follow the
// order and are compared for grouping in groupOrderLinesForDisplay), so the
// raw value stays untouched - only the rendering here is translated. Unknown
// titles render as-is.
//
// Express: matches both the old "Express Delivery 24 (100%)" wording and the
// current "Express production 24 (100%)" so lines on existing orders/drafts
// render the corrected wording too. Hours (24/48/72) and the surcharge percent
// are interpolated. The service is express PRODUCTION - the percent is the
// price surcharge, not a delivery service.
const EXPRESS_TITLE_RE =
  /^Express (?:Delivery|production) (\d+) \((\d+(?:[.,]\d+)?%)\)$/;

// Packaging: FE-generated packaging line titles (see buildLocalOrderDraft in
// useOrderActionsHandler). Known variants map to their own key; prefix
// matching below catches old/new suffix variations.
const STANDARD_PACKAGING_VARIANT_KEYS: Record<string, string> = {
  Bags: "orderLineType.standardPackagingBags",
  Jackets: "orderLineType.standardPackagingJackets",
  Sweatshirt: "orderLineType.standardPackagingSweatshirt",
  "Polo shirt": "orderLineType.standardPackagingPoloShirt",
  "T-shirt": "orderLineType.standardPackagingTShirt",
  Workwear: "orderLineType.standardPackagingWorkwear",
};

// Startup (opstart) fees: the stored line title is the raw Danish NAV text
// imported by RunStartupFeesImportAsync in the API (LogoImportAllFunctions.cs,
// `startupFeeKeys`). Keys here are the EXACT (trimmed) NAV strings mapped to
// their own translation key. Titles are order DATA - the raw value stays
// untouched everywhere else, only the rendering below is translated. The NAV
// X_100/Y_100 and X_110/Y_110 pairs share the same Danish text, so the two
// duplicated titles collapse to a single entry each.
const STARTUP_FEE_TITLE_KEYS: Record<string, string> = {
  "Opstart tryk": "orderLineType.startupPrint",
  "Opstart tryk (ifølge aft.)": "orderLineType.startupPrintByAgreement",
  "Opstart broderi": "orderLineType.startupEmbroidery",
  "Opstart tryk spilletrøje": "orderLineType.startupPrintJersey",
  "Opstart broderi (ifølge aft.)": "orderLineType.startupEmbroideryByAgreement",
  "Opstart tryk Hjelme": "orderLineType.startupPrintHelmets",
  "Opstart tryk (SB)": "orderLineType.startupPrintSb",
  "Opstart broderi (SB)": "orderLineType.startupEmbroiderySb",
  "Opstart sublimation": "orderLineType.startupSublimation",
};

export function orderLineTitleText(
  title: string | undefined,
  t: (key: string, options?: Record<string, string>) => string,
): string {
  if (!title) return "";
  const trimmed = title.trim();

  if (trimmed === "Film pr. farve")
    return t("orderListDetails.filmChargePerColor");

  const expressMatch = EXPRESS_TITLE_RE.exec(trimmed);
  if (expressMatch) {
    return t("orderLineType.expressProduction", {
      hours: expressMatch[1],
      pct: expressMatch[2],
    });
  }

  if (trimmed.startsWith("Specific Packaging")) {
    // Generated titles carry the "(QTY will be adjusted)" suffix; a bare
    // title renders without it.
    return trimmed === "Specific Packaging"
      ? t("orderLineType.specificPackagingBase")
      : t("orderLineType.specificPackaging");
  }

  if (trimmed.startsWith("Standard packaging")) {
    const variantMatch = /^Standard packaging\s*\((.+)\)$/.exec(trimmed);
    if (variantMatch) {
      const variantKey = STANDARD_PACKAGING_VARIANT_KEYS[variantMatch[1]];
      if (variantKey) return t(variantKey);
      // Unknown variant: translate the prefix, keep the raw variant.
      return t("orderLineType.standardPackagingVariant", {
        variant: variantMatch[1],
      });
    }
    return t("orderLineType.standardPackaging");
  }

  const startupFeeKey = STARTUP_FEE_TITLE_KEYS[trimmed];
  if (startupFeeKey) return t(startupFeeKey);

  return title;
}

// Admin price adjustment: an agreed price replaced the line's VENDOR price
// (the amount Stripe charges and NAV invoices), so the agreed rendering is
// only right where the row is read on that basis - admins (their totals are
// the vendor basis, and the agreed branch strikes the vendor price through)
// and vendors looking at their own prices. With "Show customer prices" on,
// or for an end customer, the row keeps its plain customer price: one
// vendor-basis figure between customer prices would misread as a customer
// price. Undefined role (profile not loaded yet) = not shown.
//
// Gated on the FE flag as well (`enabled`, default = the flag): the API's
// AutoMapper convention can send `agreedUnitPrice` on the org-scoped read
// too once the API flag is on, so without this gate a rollback of the FE
// flag alone would keep the struck-through price and the per-agreement
// variant split on the details. FE flag off = every row and grouping
// exactly as before, whatever the payload carries.
export function agreedPriceApplies(
  role: Role | undefined,
  showCustomerPrices?: boolean,
  enabled: boolean = enviroment.adminPriceAdjustmentEnabled,
): boolean {
  return (
    enabled &&
    !showCustomerPrices &&
    (role === Role.Admin || role === Role.Vendor)
  );
}

export function useAgreedPriceApplies(showCustomerPrices?: boolean): boolean {
  const { role } = useProfileContext();
  return agreedPriceApplies(role, showCustomerPrices);
}

// Which stored amount a viewer's rows and client-side sums are read from:
// the vendor amount (unitPrice / price) for a vendor on its own prices - as
// always - and, under the flag, for an admin without "Show customer prices"
// too, so the rows, the summed totals and the stored totals an adjustment
// produced (SUM(qty x (agreed ?? vendor price))) all sit on ONE basis. Before
// the flag an admin always read the customer amount (adjustedUnitPrice /
// adjustedPrice), which put customer-basis rows next to vendor-basis totals
// on an adjusted order - the checkbox now means the same for admins as for
// vendors. Flag off = the old rule, byte-identical.
export function readsVendorBasis(
  role: Role | undefined,
  showCustomerPrices?: boolean,
  enabled: boolean = enviroment.adminPriceAdjustmentEnabled,
): boolean {
  if (role === Role.Vendor && !showCustomerPrices) return true;
  return agreedPriceApplies(role, showCustomerPrices, enabled);
}

const useOrderLinePrice = (
  orderline: OrderLine,
  showCustomerPrices?: boolean
) => {
  const { role } = useProfileContext();

  // When showCustomerPrices is true, always show the adjusted price.
  // Otherwise, Vendors (Role.Vendor = 1) should see the vendor price (orderline.price)
  // and Customers (Role.EndUser = 0) and others should see customer price (orderline.adjustedPrice)
  // - except an admin under the price-adjustment flag, who reads the vendor
  // basis like a vendor (readsVendorBasis), matching the strike-through below.
  const displayUnitPrice = readsVendorBasis(role, showCustomerPrices)
    ? orderline.unitPrice
    : orderline.adjustedUnitPrice;

  const displayPrice = displayUnitPrice;

  // The agreed price only where the row is on the vendor basis (see
  // agreedPriceApplies); undefined = the line renders exactly as before.
  const agreedUnitPrice = agreedPriceApplies(role, showCustomerPrices)
    ? orderline.product?.agreedUnitPrice
    : undefined;

  return { displayPrice, displayUnitPrice, agreedUnitPrice };
};

export interface OrderLineHideColumns {
  displayNo?: boolean;
  image?: boolean;
  itemNumber?: boolean;
  title?: boolean;
  type?: boolean;
  unitPrice?: boolean;
  quantity?: boolean;
  price?: boolean;
  subTotal?: boolean;
}

export const OrderLineHeaders = ({ hide }: { hide?: OrderLineHideColumns }) => {
  const { t } = useTranslation();
  return (
    <tr>
      {!hide?.displayNo && (
        <th className="w-1/12 text-left px-2">{t("orderListDetails.colNo")}</th>
      )}
      {!hide?.image && (
        <th className="w-1/12 text-left px-2">{t("image")}</th>
      )}
      {!hide?.itemNumber && (
        <th className="w-1/12 text-left px-2">{t("item_no")}</th>
      )}
      {!hide?.title && <th className="w-2/12 text-left px-2">{t("title")}</th>}
      {!hide?.type && <th className="w-1/12 text-left px-2">{t("type")}</th>}
      {!hide?.unitPrice && (
        <th className="w-1/12 text-right px-2">
          {t("orderListDetails.colPricePerPiece")}
        </th>
      )}
      {!hide?.quantity && (
        <th className="w-1/12 text-right px-2">
          {t("orderListDetails.colQty")}
        </th>
      )}
      {!hide?.price && (
        <th className="w-1/12 text-right px-2">
          {t("orderListDetails.colQtyPrice")}
        </th>
      )}
      {!hide?.subTotal && (
        <th className="w-1/12 text-right px-2">
          {t("orderListDetails.colTotal")}
        </th>
      )}
    </tr>
  );
};

export interface SharedOrderLineDataProps {
  displayNo?: string;
  hide?: OrderLineHideColumns;
  bold?: boolean;
  currency?: string;
  isFirstItem?: boolean;
  showCustomerPrices?: boolean;
  onRowClick?: () => void;
  leadingNote?: ReactNode;
  trailingNote?: ReactNode;
}

export interface OrderLineDataProps extends SharedOrderLineDataProps {
  title: string;
  imageSrc?: string;
  itemNumber?: string;
  type?: string;
  price?: number;
  unitPrice?: number;
  quantity?: number;
  minQuantity?: number;
  subTotal?: number;
  // Admin price adjustment (flag REACT_APP_ADMIN_PRICE_ADJUSTMENT_ENABLED):
  // the agreed unit price that replaced the stored one. Only the admin read
  // carries it while the API flag is on - undefined = today's rendering.
  agreedUnitPrice?: number;
  // The price the agreement replaced: the line's stored vendor price
  // (orderline.unitPrice, "Our price" in the adjust dialog). `price` may be
  // the customer price for non-vendor roles, so it is not the right number
  // to strike through. Falls back to `price` when absent.
  ourUnitPrice?: number;
}

export default function OrderLineData({
  displayNo,
  imageSrc,
  itemNumber: sku,
  title,
  type,
  price,
  unitPrice,
  currency,
  quantity,
  minQuantity,
  subTotal,
  hide,
  bold,
  isFirstItem,
  onRowClick,
  leadingNote,
  trailingNote,
  agreedUnitPrice,
  ourUnitPrice,
}: OrderLineDataProps) {
  const { t } = useTranslation();
  const [isImageHovered, setIsImageHovered] = useState(false);
  const [imageRect, setImageRect] = useState<DOMRect | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleMouseEnter = (e: React.MouseEvent<HTMLImageElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setImageRect(rect);
    setIsImageHovered(true);
    // Start animation after a brief delay to ensure the modal is rendered
    setTimeout(() => setIsAnimating(true), 10);
  };

  const handleMouseLeave = () => {
    setIsAnimating(false);
    // Keep the modal visible during the scale-down animation
    setTimeout(() => setIsImageHovered(false), 150);
  };

  // An agreed price (admin adjustment) is the line's effective price: it
  // drives the piece price and the line total, while the vendor price it
  // replaced stays visible struck through as documentation.
  const hasAgreedPrice =
    agreedUnitPrice !== undefined && agreedUnitPrice !== null;
  const replacedPrice =
    ourUnitPrice !== undefined && ourUnitPrice !== null
      ? ourUnitPrice
      : price || 0;
  const actualPrice = hasAgreedPrice ? agreedUnitPrice : price || 0,
    actualUnitPrice = hasAgreedPrice ? agreedUnitPrice : unitPrice || 0,
    actualQuantity = quantity || 0,
    actualSubTotal = hasAgreedPrice
      ? actualQuantity * actualPrice
      : subTotal || actualQuantity * actualPrice;

  const cleanedDisplayNo = displayNo?.replace(/\s+-\s+/g, "-") || "";
  const shouldHideLineNo = cleanedDisplayNo.split("-").length >= 3;

  // Determine if this is a main item (e.g., "01") or sub item (e.g., "01-1", "01-2")
  const isMainItem = displayNo && !displayNo.includes("-");
  const isSubItem = displayNo && displayNo.includes("-");

  const mainItemClass = isMainItem ? "font-bold bg-white px-2" : "";
  const subItemClass = isSubItem
    ? type === "AdditionalFee" ||
      sku === "From Storage" ||
      type === "Additional Fee"
      ? "bg-gray-100 px-2"
      : "font-bold bg-gray-100 px-2"
    : "";

  const rowHeightClass = isMainItem ? "h-18" : isSubItem ? "h-10" : "h-10";

  const clickableClass = onRowClick ? "cursor-pointer hover:bg-gray-50" : "";

  return (
    <tr
      className={`min-h-32 ${rowHeightClass} ${clickableClass}`}
      onClick={onRowClick}
    >
      {!hide?.displayNo && (
        <td className={`text-left ${mainItemClass}${subItemClass}`}>
          {leadingNote}
          {!shouldHideLineNo ? cleanedDisplayNo : ""}
        </td>
      )}
      {!hide?.image && (
        <td className={`place-items-left ${mainItemClass}${subItemClass}`}>
          {imageSrc && (
            <>
              <img
                src={imageSrc}
                className="my-2 object-contain max-h-[50px] max-w-[80px] bg-white rounded hover:border hover:p-1 transition-all duration-150 ease-out"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              />
              {isImageHovered &&
                imageRect &&
                (() => {
                  const padding = 5; // Corresponds to p-1 (4px) and border (1px)
                  const targetSize = 350; // The target size in pixels for the longest side of the zoomed image

                  const newWidth = imageRect.width + padding * 2;
                  const newHeight = imageRect.height + padding * 2;
                  const newLeft = imageRect.left - padding;
                  const newTop = imageRect.top - padding;

                  const longestSide = Math.max(newWidth, newHeight);
                  const scale = targetSize / longestSide;

                  return (
                    <div className="fixed inset-0 pointer-events-none z-[9999]">
                      <div
                        className="transition-all duration-200 ease-out flex justify-center items-center"
                        style={{
                          position: "absolute",
                          left: newLeft,
                          top: newTop,
                          width: newWidth,
                          height: newHeight,
                          transformOrigin: isFirstItem
                            ? "top left"
                            : "center left",
                          transform: isAnimating
                            ? `scale(${scale})`
                            : "scale(1)",
                        }}
                      >
                        <img
                          src={imageSrc}
                          className="object-contain bg-white rounded border shadow-2xl"
                          style={{
                            width: "100%",
                            height: "100%",
                            padding: `${padding}px`,
                          }}
                        />
                      </div>
                    </div>
                  );
                })()}
            </>
          )}
        </td>
      )}
      {!hide?.itemNumber && (
        <td className={`text-left ${mainItemClass}${subItemClass}`}>
          {sku || ""}
        </td>
      )}
      {!hide?.title && (
        <td className={`text-left ${mainItemClass}${subItemClass}`}>
          {orderLineTitleText(title, t)}
          {trailingNote}
        </td>
      )}
      {!hide?.type && (
        <td className={`text-left ${mainItemClass}${subItemClass}`}>
          {orderLineTypeText(type, t)}
        </td>
      )}
      {!hide?.unitPrice && (
        <td className={`text-right ${mainItemClass}${subItemClass}`}>
          {hasAgreedPrice
            ? getAgreedPriceString(actualUnitPrice, currency)
            : getPriceString(actualUnitPrice, currency)}
        </td>
      )}
      {!hide?.quantity && (
        <td className={`text-right ${mainItemClass}${subItemClass}`}>
          {actualQuantity || ""}
        </td>
      )}
      {!hide?.price && (
        <td className={`text-right ${mainItemClass}${subItemClass}`}>
          {hasAgreedPrice ? (
            <span
              className={"inline-flex flex-col items-end"}
              data-testid="agreed-price-cell"
            >
              <s
                className={"text-gray-500 font-normal"}
                title={t("orderPriceAdjustment.ourPrice")}
              >
                {getAgreedPriceString(replacedPrice, currency)}
              </s>
              <span title={t("orderPriceAdjustment.agreedPrice")}>
                {getAgreedPriceString(actualPrice, currency)}
              </span>
            </span>
          ) : (
            getPriceString(actualPrice, currency)
          )}
        </td>
      )}
      {!hide?.subTotal && (
        <td className={`text-right ${mainItemClass}${subItemClass}`}>
          {(hasAgreedPrice
            ? getAgreedPriceString(actualSubTotal, currency)
            : getPriceString(actualSubTotal, currency)) +
            (minQuantity ? ` (${minQuantity})` : "")}
        </td>
      )}
    </tr>
  );
}

export interface BaseOrderLineDataProps extends SharedOrderLineDataProps {
  orderline: OrderLine;
}
export function BaseOrderLineData({
  displayNo,
  orderline,
  hide,
  currency,
  isFirstItem,
  showCustomerPrices,
  onRowClick,
  leadingNote,
  trailingNote,
}: BaseOrderLineDataProps) {
  const { displayPrice, displayUnitPrice, agreedUnitPrice } = useOrderLinePrice(
    orderline,
    showCustomerPrices
  );

  return (
    <OrderLineData
      displayNo={displayNo ?? orderline.displayNo}
      hide={hide}
      itemNumber={orderline.product.productNo}
      title={orderline.product.productName}
      type={orderline.type}
      quantity={orderline.quantity}
      price={displayPrice}
      unitPrice={displayUnitPrice}
      currency={currency}
      isFirstItem={isFirstItem}
      onRowClick={onRowClick}
      leadingNote={leadingNote}
      trailingNote={trailingNote}
      agreedUnitPrice={agreedUnitPrice}
      ourUnitPrice={orderline.unitPrice}
    />
  );
}

export interface DesignOrderLineDataProps extends BaseOrderLineDataProps {
  orderline: OrderLineDesign;
}
export function DesignOrderLineData({
  displayNo,
  orderline,
  hide,
  currency,
  isFirstItem,
  showCustomerPrices,
  onRowClick,
  leadingNote,
  trailingNote,
}: DesignOrderLineDataProps) {
  const { displayPrice, displayUnitPrice, agreedUnitPrice } = useOrderLinePrice(
    orderline,
    showCustomerPrices
  );

  return (
    <OrderLineData
      bold={true}
      displayNo={displayNo ?? orderline.displayNo}
      hide={hide}
      imageSrc={orderline.displayImage}
      itemNumber={orderline.product.productNo}
      title={orderline.product.productName}
      type={orderline.product.productType}
      quantity={orderline.quantity}
      price={displayPrice}
      unitPrice={displayUnitPrice}
      currency={currency}
      isFirstItem={isFirstItem}
      onRowClick={onRowClick}
      leadingNote={leadingNote}
      trailingNote={trailingNote}
      agreedUnitPrice={agreedUnitPrice}
      ourUnitPrice={orderline.unitPrice}
    />
  );
}

export interface LogoOrderLineDataProps extends BaseOrderLineDataProps {
  orderline: OrderLineLogo;
}
export function LogoOrderLineData({
  displayNo,
  orderline,
  hide,
  currency,
  isFirstItem,
  showCustomerPrices,
  onRowClick,
  leadingNote,
  trailingNote,
}: LogoOrderLineDataProps) {
  const { displayPrice, displayUnitPrice, agreedUnitPrice } = useOrderLinePrice(
    orderline,
    showCustomerPrices
  );

  return (
    <OrderLineData
      displayNo={displayNo ?? orderline.displayNo}
      hide={hide}
      imageSrc={orderline.displayImage}
      itemNumber={orderline.product.productNo}
      title={orderline.product.productName}
      // baseLogo is absent while the order-detail screens render the
      // placeholder row from the order-list cache (list lines carry only
      // product data, no logo object) - getTypeText would crash on it.
      // Fall back to the generic product type until the real fetch lands.
      type={orderline.baseLogo ? getTypeText(orderline.baseLogo) : orderline.type}
      quantity={orderline.quantity}
      price={displayPrice}
      unitPrice={displayUnitPrice}
      currency={currency}
      isFirstItem={isFirstItem}
      onRowClick={onRowClick}
      leadingNote={leadingNote}
      trailingNote={trailingNote}
      agreedUnitPrice={agreedUnitPrice}
      ourUnitPrice={orderline.unitPrice}
    />
  );
}

export interface GenericOrderLineDataProps extends SharedOrderLineDataProps {
  orderline: OrderLine;
}
export const GenericOrderLine = ({
  hide,
  displayNo,
  orderline,
  currency,
  isFirstItem,
  showCustomerPrices,
  onRowClick,
  leadingNote,
  trailingNote,
}: GenericOrderLineDataProps) => {
  switch (orderline.product.productType) {
    case "Design":
      return (
        <DesignOrderLineData
          displayNo={displayNo || orderline.displayNo}
          hide={hide}
          orderline={orderline as OrderLineDesign}
          currency={currency}
          isFirstItem={isFirstItem}
          showCustomerPrices={showCustomerPrices}
          onRowClick={onRowClick}
          leadingNote={leadingNote}
          trailingNote={trailingNote}
        />
      );
    case "Logo":
      return (
        <LogoOrderLineData
          displayNo={displayNo || orderline.displayNo}
          hide={hide}
          orderline={orderline as OrderLineLogo}
          currency={currency}
          isFirstItem={isFirstItem}
          showCustomerPrices={showCustomerPrices}
          onRowClick={onRowClick}
          leadingNote={leadingNote}
          trailingNote={trailingNote}
        />
      );
    default:
      return (
        <BaseOrderLineData
          displayNo={displayNo || orderline.displayNo}
          hide={hide}
          orderline={orderline}
          currency={currency}
          isFirstItem={isFirstItem}
          showCustomerPrices={showCustomerPrices}
          onRowClick={onRowClick}
          leadingNote={leadingNote}
          trailingNote={trailingNote}
        />
      );
  }
};
